import { forwardRef } from "react";

import { cn, platformProps } from "../../lib";
import type { ScrollViewProps } from "./types";

/**
 * ScrollView - Web implementation
 *
 * A scrollable `div`. `className` styles the scroll container;
 * `contentContainerClassName` styles an inner wrapper, mirroring the native
 * ScrollView's content-container model: a flex column, or for `horizontal` a
 * flex row sized to its content (`w-max`), so children keep their width and
 * the wrapper's padding scrolls into view.
 */
export const ScrollView = forwardRef<HTMLDivElement, ScrollViewProps>(
  (
    {
      children,
      className,
      contentContainerClassName,
      horizontal = false,
      showsScrollIndicator = true,
      testID,
    },
    ref,
  ) => {
    // The inner wrapper carries content-container classes (or the horizontal
    // track) — skip it otherwise so it adds no layout node.
    const content =
      horizontal || contentContainerClassName ? (
        <div
          className={cn(
            horizontal ? "flex w-max flex-row" : "flex flex-col",
            contentContainerClassName,
          )}
        >
          {children}
        </div>
      ) : (
        children
      );

    return (
      <div
        ref={ref}
        {...platformProps({ testID })}
        className={cn(
          horizontal ? "overflow-x-auto" : "overflow-y-auto",
          !showsScrollIndicator && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        {content}
      </div>
    );
  },
);

ScrollView.displayName = "ScrollView";
